import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useIsMobile } from '../hooks/useIsMobile'
import { useUiPreference } from '../hooks/useUiPreference'
import {
  resolveSidebarMode,
  SIDEBAR_PEEK_DELAY_MS,
  type SidebarMode,
  type SidebarOverride
} from '../components/layout/sidebarState'

export interface SidebarContextValue {
  mode: SidebarMode
  /** Drawer off-canvas (abaixo de lg) */
  drawerOpen: boolean
  openDrawer: () => void
  closeDrawer: () => void
  /**
   * Fixa/recolhe. Com override ativo (dentro do editor) inverte só o
   * override — a preferência global não muda. Fora, inverte e persiste a
   * preferência. Sempre encerra um peek em andamento.
   */
  toggle: () => void
  /** Editor pede 'collapsed' enquanto está montado; null devolve à preferência */
  setOverride: (value: SidebarOverride) => void
  /** Abre o peek após `delayMs` (default 150ms). 0 = imediato (foco via teclado). */
  startPeek: (delayMs?: number) => void
  endPeek: () => void
}

const SidebarContext = createContext<SidebarContextValue | null>(null)

export const SidebarProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isDesktop } = useIsMobile()
  const [collapsedPref, setCollapsedPref] = useUiPreference<boolean>('sidebarCollapsed', false)
  const [override, setOverrideState] = useState<SidebarOverride>(null)
  const [peeking, setPeeking] = useState(false)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const peekTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearPeekTimer = () => {
    if (peekTimer.current) {
      clearTimeout(peekTimer.current)
      peekTimer.current = null
    }
  }

  const startPeek = useCallback((delayMs: number = SIDEBAR_PEEK_DELAY_MS) => {
    clearPeekTimer()
    if (delayMs <= 0) {
      setPeeking(true)
      return
    }
    peekTimer.current = setTimeout(() => {
      peekTimer.current = null
      setPeeking(true)
    }, delayMs)
  }, [])

  const endPeek = useCallback(() => {
    clearPeekTimer()
    setPeeking(false)
  }, [])

  useEffect(() => () => clearPeekTimer(), [])

  const setOverride = useCallback((value: SidebarOverride) => {
    setOverrideState(value)
    endPeek()
  }, [endPeek])

  const toggle = useCallback(() => {
    endPeek()
    if (override !== null) {
      setOverrideState(override === 'collapsed' ? 'expanded' : 'collapsed')
    } else {
      setCollapsedPref(!collapsedPref)
    }
  }, [override, collapsedPref, setCollapsedPref, endPeek])

  const openDrawer = useCallback(() => setDrawerOpen(true), [])
  const closeDrawer = useCallback(() => setDrawerOpen(false), [])

  const mode = resolveSidebarMode({ isDesktop, collapsedPref, override, peeking })

  const value = useMemo<SidebarContextValue>(
    () => ({ mode, drawerOpen, openDrawer, closeDrawer, toggle, setOverride, startPeek, endPeek }),
    [mode, drawerOpen, openDrawer, closeDrawer, toggle, setOverride, startPeek, endPeek]
  )

  return <SidebarContext.Provider value={value}>{children}</SidebarContext.Provider>
}

export function useSidebar(): SidebarContextValue {
  const ctx = useContext(SidebarContext)
  if (!ctx) throw new Error('useSidebar precisa estar dentro de <SidebarProvider>')
  return ctx
}
